<template>
  <div class="p-3">
    <form
      class="border rounded p-3 bg-body-tertiary"
      @submit.prevent="handleCreateBankingInstitution"
    >
      <h6 class="mb-3">Create Banking Institution</h6>

      <div class="mb-3">
        <label class="form-label" for="banking-institution-name"> Name </label>
        <input
          id="banking-institution-name"
          v-model="bankingInstitutionForm.name"
          class="form-control"
          type="text"
          maxlength="255"
          placeholder="e.g. Manual Institution"
          :disabled="creatingBankingInstitution"
          required
        />
      </div>

      <div class="mb-3">
        <label class="form-label" for="banking-institution-type"> Type </label>
        <select
          id="banking-institution-type"
          v-model="bankingInstitutionForm.type"
          class="form-select"
          :disabled="creatingBankingInstitution"
          required
        >
          <option value="Checking">Checking</option>
          <option value="Savings">Savings</option>
          <option value="Credit Card">Credit Card</option>
        </select>
      </div>

      <div class="mb-3">
        <label class="form-label" for="banking-institution-source">
          Source
        </label>
        <select
          id="banking-institution-source"
          v-model="bankingInstitutionForm.source"
          class="form-select"
          :disabled="creatingBankingInstitution"
          required
        >
          <option value="Excel">Excel</option>
        </select>
      </div>

      <div class="mb-3">
        <label class="form-label" for="banking-institution-acct-last-4">
          Account Last 4
        </label>
        <input
          id="banking-institution-acct-last-4"
          v-model="bankingInstitutionForm.acctLast4"
          class="form-control"
          type="text"
          maxlength="4"
          inputmode="numeric"
          pattern="[0-9]{4}"
          placeholder="e.g. 1234"
          :disabled="creatingBankingInstitution"
          required
        />
      </div>

      <BButton
        type="submit"
        variant="primary"
        :disabled="!canSubmitBankingInstitutionForm"
      >
        {{
          creatingBankingInstitution
            ? "Creating..."
            : "Create Banking Institution"
        }}
      </BButton>
    </form>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import api from "@/services/api";
import { userSession as userSessNs } from "@/store/namespaces.js";
import { BButton } from "bootstrap-vue-next";

export default {
  name: "CreateView",
  components: {
    BButton,
  },
  props: {
    acctUid: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      creatingBankingInstitution: false,
      bankingInstitutionForm: {
        name: "",
        type: "Checking",
        source: "Excel",
        acctLast4: "",
      },
    };
  },
  computed: {
    canSubmitBankingInstitutionForm() {
      const name = this.bankingInstitutionForm.name?.trim();
      const type = this.bankingInstitutionForm.type?.trim();
      const source = this.bankingInstitutionForm.source;
      const acctLast4 = this.bankingInstitutionForm.acctLast4?.trim();

      return (
        !this.creatingBankingInstitution &&
        !!this.acctUid &&
        /^\d{4}$/.test(acctLast4) &&
        !!name &&
        ["Checking", "Savings", "Credit Card"].includes(type) &&
        source === "Excel"
      );
    },
  },
  methods: {
    ...mapActions({
      getUserInfo: `${userSessNs}/getUserInfo`,
    }),
    async handleCreateBankingInstitution() {
      if (!this.canSubmitBankingInstitutionForm) {
        this.$notify.notifyError(
          "Provide name, type, source, and account last 4, then select an account."
        );
        return;
      }

      try {
        this.creatingBankingInstitution = true;

        const userInfo = await this.getUserInfo();
        const userUid = userInfo?.id;

        if (!userUid) {
          throw new Error("Missing user UID for banking institution create.");
        }

        await api.post("/banking-institutions", {
          name: this.bankingInstitutionForm.name.trim(),
          type: this.bankingInstitutionForm.type.trim(),
          source: this.bankingInstitutionForm.source,
          acctUid: this.acctUid,
          acctLast4: this.bankingInstitutionForm.acctLast4.trim(),
          userUid,
        });

        this.bankingInstitutionForm.name = "";
        this.bankingInstitutionForm.type = "Checking";
        this.bankingInstitutionForm.source = "Excel";
        this.bankingInstitutionForm.acctLast4 = "";

        this.$notify.notifySuccess("Banking institution created successfully.");
      } catch (error) {
        this.$notify.notifyError(
          "Unable to create banking institution. Please try again."
        );
        console.error("Unable to create banking institution.", error);
      } finally {
        this.creatingBankingInstitution = false;
      }
    },
  },
};
</script>
